// @ts-strict-ignore
import React, { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Input,
  InputChipList,
  InputChipListChip,
  InputChipListProps,
  Switch,
} from '@node-space/ui-components'
import { ApiKeyRequestForm } from 'types/apiKeys'
import { apiKeySchema } from './schema'
import { isIPRangeValid, isIPValid } from './validation'

export type ApiKeyCreateForm = {
  payload?: ApiKeyRequestForm
  onSubmit: SubmitHandler<{
    description: string
    withdrawals: boolean
    payroll: boolean
    allowedIps: InputChipListChip[]
  }>
  isLoading?: boolean
  requestError?: string
  isEditMode?: boolean
  dismissAction?: () => void
  enablePayrollRole?: boolean
}

const ApiKeyCreateForm = ({
  payload,
  onSubmit,
  isLoading = false,
  isEditMode = false,
  requestError,
  dismissAction,
  enablePayrollRole,
}: ApiKeyCreateForm) => {
  const { t } = useTranslation()
  const [isAllowedIpsError, setIsAllowedIpsError] = useState<string | null>(null)

  const {
    control,
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    clearErrors,
  } = useForm<{
    description: string
    withdrawals: boolean
    payroll: boolean
    allowedIps: InputChipListChip[]
  }>({
    defaultValues: {
      allowedIps: payload?.allowedIps ?? [],
      description: payload?.description ?? '',
      withdrawals: payload?.withdrawals ?? true,
      payroll: enablePayrollRole,
    },
    resolver: yupResolver(apiKeySchema()),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const [allowedIps, withdrawals] = useWatch({
    control,
    name: ['allowedIps', 'withdrawals'],
  })

  const isFormValid = (isValid && !!allowedIps.length) || (isValid && !withdrawals)

  const handleAllowedIpsChange = (values: InputChipListProps['chips']) => {
    const lastAddedIp = values[values.length - 1]

    if (lastAddedIp) {
      const isCIDR = lastAddedIp.label.includes('/')

      if (isCIDR && !isIPRangeValid(lastAddedIp.label)) {
        setIsAllowedIpsError(t('manageApiKeys.invalidIPRange'))
        return
      }

      if (!isCIDR && !isIPValid(lastAddedIp.label)) {
        setIsAllowedIpsError(t('manageApiKeys.invalidIPEntry'))
        return
      }
    }

    clearErrors('allowedIps')
    setIsAllowedIpsError(null)
    setValue('allowedIps', values)
  }

  return (
    <Box tag="form" flexCol gapY={16} data-testid="apikey-create" onSubmit={handleSubmit(onSubmit)}>
      <Box paddingT={24} gapY={16} flexCol>
        <Input
          {...register('description')}
          onChange={e => setValue('description', e.target.value, { shouldValidate: true })}
          label={t('name')}
          placeholder={t('manageApiKeys.keyDescription')}
          data-testid="api-key-name"
          error={Boolean(errors?.description)}
          errorText={errors?.description?.message}
          disabled={isLoading}
        />

        <InputChipList
          label={t('manageApiKeys.allowedIpAddressInputLabel')}
          placeholderText={t('manageApiKeys.allowedIpAddressInputPlaceholder')}
          optionalText={!withdrawals && t('optionalLabel')}
          name="allowedIps"
          data-testid="api-key-allowed-ips"
          onChange={handleAllowedIpsChange}
          chips={allowedIps}
          error={Boolean(isAllowedIpsError) || Boolean(errors?.allowedIps)}
          errorText={isAllowedIpsError || errors?.allowedIps?.message}
          disabled={isLoading}
          inputMinWidth="400px"
        />

        <Box flexCol gapY={16} width="full">
          <p className="text-label text-foreground-prominent">
            {t('manageApiKeys.enableAdditionally')}
          </p>
          <Box flex gapX={8} width="full">
            <Switch
              value={Boolean(withdrawals)}
              onChange={value => {
                setValue('withdrawals', value)
              }}
            />
            <p className="mr-2 text-body-regular text-foreground-prominent">
              {t('manageApiKeys.enableWithdrawalsLabel')}
            </p>
          </Box>
        </Box>
        {!requestError && (
          <Box className="text-body-regular text-foreground-action-destructive">{requestError}</Box>
        )}
      </Box>

      <Box flex justify="between" paddingT={8}>
        <Button variant="secondary" onClick={dismissAction}>
          {t('cancel')}
        </Button>

        <Button
          data-testid={t('continue')}
          variant="primary"
          loading={isLoading}
          disabled={!isFormValid}
          type="submit"
        >
          {isEditMode ? t('save') : t('continue')}
        </Button>
      </Box>
    </Box>
  )
}

export default React.memo(ApiKeyCreateForm)
